import { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import styled from '@/styles';
import { DependabilitySignal } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import ActivityFeed from './ActivityFeed';
import useDependabilityEvents from './useDependabilityEvents';
import { InfoTipIcon20, XMarkIcon } from './icons';

const SignalList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});
// Row geometry per the breakdown frame (37858-32014). GRAVY-10233: all three
// cells are FIXED width so every row is identical — a single-line label column,
// then a track that starts and ends at the same x on every row (only its fill
// varies), then the value. The old `space-between` + `flex:1 1 auto` track let
// the track slide left/right by however wide each row's value happened to be
// ("Moderate" vs "0"), so the bars never lined up; and the 120px label wrapped
// "No-show penalty" / "Late-drop penalty" onto two lines, pushing those bars down.
const SignalRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});
const SignalLabel = styled('div', {
  fontSize: '$lg',
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
  // fixed + nowrap: every label stays on ONE line in a same-width column so the
  // track after it always starts at the same x (150px fits the longest label,
  // "Late-drop penalty", at $lg without wrapping).
  flex: '0 0 150px',
  whiteSpace: 'nowrap',
});
const MeterTrack = styled('div', {
  // fixed width (not flex-grow): the track is the same length and same start on
  // every row, so the bars line up regardless of the value cell's width.
  flex: '0 0 320px',
  height: '6px',
  borderRadius: '100px',
  backgroundColor: dependabilityV2Colors.depMeterTrack,
  overflow: 'hidden',
});
const MeterFill = styled('div', {
  height: '6px',
  borderRadius: '100px',
});
const SignalValue = styled('div', {
  fontSize: '$lg',
  lineHeight: 1.4,
  fontWeight: 700,
  // takes the remaining width (constant, since label + track are fixed), so the
  // value is right-aligned in a consistent cell. display_values ("-11.2 pts",
  // "Moderate") never wrap.
  flex: '1 1 auto',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  variants: {
    status: {
      ok: { color: dependabilityV2Colors.depPositive },
      warn: { color: dependabilityV2Colors.depWarnText },
    },
  },
});
const Footer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  // 40px section gap above the divider per the frame's container gap
  marginTop: '40px',
  paddingTop: '16px',
  borderTop: `1px solid ${dependabilityV2Colors.depBorder}`,
  fontSize: '$md',
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
});

interface Props {
  workerId: number;
  isOpen: boolean;
  onClose: () => void;
  signals?: DependabilitySignal[];
  completedShifts?: number;
  scoringMode?: 'activation' | 'reliability';
}

const PER_PAGE = 20;

const fillColor = (status: DependabilitySignal['status']) =>
  status === 'warn'
    ? dependabilityV2Colors.depFeedNeutral
    : dependabilityV2Colors.depFeedPositive;

const ScoreHistoryModal = ({
  workerId,
  isOpen,
  onClose,
  signals = [],
  completedShifts = 0,
  scoringMode = 'reliability',
}: Props) => {
  const hasSignals = signals.length > 0;
  const { events, page, loading, loaded, error, loadPage, retry, hasMore } =
    useDependabilityEvents(workerId, PER_PAGE);

  useEffect(() => {
    // event history is only the fallback body for payloads without signals
    if (isOpen && !hasSignals) loadPage(1);
  }, [isOpen, hasSignals, loadPage]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      // frame 37858-32014: 650w, radius 8, flat shadow, 70% scrim, 24px
      // padding with 40px gaps between title / body / footer
      PaperProps={{
        sx: {
          width: 650,
          maxWidth: '95vw',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      }}
    >
      <DialogTitle
        sx={{
          padding: '24px 24px 0',
          fontSize: 18,
          fontWeight: 600,
          color: dependabilityV2Colors.depInk,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Dependability score breakdown
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: dependabilityV2Colors.depInk }}
        >
          <XMarkIcon />
        </IconButton>
      </DialogTitle>
      {/* !important: MUI forces paddingTop 0 on a DialogContent that follows
          a DialogTitle via a two-class sibling selector that outranks sx */}
      <DialogContent
        sx={{ padding: '40px 24px 24px', paddingTop: '40px !important' }}
      >
        {hasSignals ? (
          <>
            <SignalList data-testid="signal-breakdown">
              {signals.map((signal) => (
                <SignalRow
                  key={signal.key}
                  data-testid={`signal-${signal.key}`}
                >
                  <SignalLabel>{signal.label}</SignalLabel>
                  <MeterTrack>
                    <MeterFill
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(100, signal.percent ?? 0),
                        )}%`,
                        backgroundColor: fillColor(signal.status),
                      }}
                    />
                  </MeterTrack>
                  <SignalValue status={signal.status}>
                    {signal.display_value}
                  </SignalValue>
                </SignalRow>
              ))}
            </SignalList>
            <Footer>
              <InfoTipIcon20 />
              {scoringMode === 'activation'
                ? 'Activation score, based on onboarding progress'
                : `Based on ${completedShifts} shifts`}
            </Footer>
          </>
        ) : (
          <ActivityFeed
            events={events}
            hasMore={hasMore}
            loading={loading || !loaded}
            // A failed events fetch must surface as a retryable note, not the
            // false "No score activity yet" (matches RecentActivityModal).
            // Retry targets the page that was actually last ATTEMPTED (fix round
            // 5), so a failed reopen page-1 refresh is retried as page 1, not
            // appended as page N+1 from a stale prior-session page.
            error={error}
            onRetry={retry}
            onViewMore={() => loadPage(page + 1)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScoreHistoryModal;
